import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchActiveLegalDocument,
  fetchAnnouncedLegalDocument,
  LegalDocument,
  LegalDocumentType,
} from '../services/legalDocuments';
import termsFallback from '../conditions/terns_and_conditions.txt?raw';
import privacyFallback from '../conditions/confidential_policy.txt?raw';
import cookiesFallback from '../conditions/cookies_policy.txt?raw';

interface LegalDocumentContentProps {
  documentType: LegalDocumentType;
}

const fallbackDocuments: Record<LegalDocumentType, { title: string; effectiveDate: string; content: string }> = {
  TERMS: {
    title: 'Termeni și Condiții – roade.ro',
    effectiveDate: '12.10.2026',
    content: termsFallback,
  },
  PRIVACY: {
    title: 'Politica de Confidențialitate – roade.ro (GDPR)',
    effectiveDate: '12.10.2026',
    content: privacyFallback,
  },
  COOKIES: {
    title: 'Politica privind cookie-urile – roade.ro',
    effectiveDate: '12.10.2026',
    content: cookiesFallback,
  },
};

type MarkdownBlock =
  | { type: 'heading2' | 'heading3' | 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] };

const parseTableRow = (line: string): string[] => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => cell.trim().replace(/\\\|/g, '|'));

const isTableDelimiter = (line: string): boolean => {
  const cells = parseTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

export const parseLegalMarkdown = (content: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = [];
  const lines = content.split(/\r?\n/);

  for (let index = 0; index < lines.length;) {
    const line = lines[index].trim();
    if (!line) {
      index += 1;
      continue;
    }

    if (line.startsWith('## ')) {
      blocks.push({ type: 'heading2', text: line.slice(3) });
      index += 1;
      continue;
    }

    if (line.startsWith('### ')) {
      blocks.push({ type: 'heading3', text: line.slice(4) });
      index += 1;
      continue;
    }

    if (line.startsWith('- ')) {
      const items: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith('- ')) {
        items.push(lines[index].trim().slice(2));
        index += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    if (line.startsWith('|') && index + 1 < lines.length && isTableDelimiter(lines[index + 1])) {
      const headers = parseTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].trim().startsWith('|')) {
        const row = parseTableRow(lines[index]);
        rows.push(headers.map((_, cellIndex) => row[cellIndex] ?? ''));
        index += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    const paragraphLines = [line];
    index += 1;
    while (index < lines.length && lines[index].trim()) {
      const nextLine = lines[index].trim();
      if (nextLine.startsWith('## ') || nextLine.startsWith('### ') || nextLine.startsWith('- ')) {
        break;
      }
      paragraphLines.push(nextLine);
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraphLines.join(' ') });
  }

  return blocks;
};

const renderSections = (blocks: MarkdownBlock[]) => {
  const sections: React.ReactNode[][] = [];
  let currentSection: React.ReactNode[] = [];

  blocks.forEach((block, index) => {
    if (block.type === 'heading2' && currentSection.length > 0) {
      sections.push(currentSection);
      currentSection = [];
    }

    if (block.type === 'heading2') {
      currentSection.push(<h2 key={index}>{block.text}</h2>);
    } else if (block.type === 'heading3') {
      currentSection.push(<h3 key={index}>{block.text}</h3>);
    } else if (block.type === 'list') {
      currentSection.push(
        <ul key={index}>
          {block.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}
        </ul>
      );
    } else if (block.type === 'table') {
      currentSection.push(
        <div className="legal-table-scroll" key={index} tabIndex={0}>
          <table className="legal-table">
            <thead>
              <tr>{block.headers.map((header, cellIndex) => <th key={cellIndex} scope="col">{header}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else {
      currentSection.push(<p key={index}>{block.text}</p>);
    }
  });

  if (currentSection.length > 0) {
    sections.push(currentSection);
  }

  return sections.map((section, index) => (
    <section className="legal-section" key={index}>{section}</section>
  ));
};

export const LegalMarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  const blocks = useMemo(() => parseLegalMarkdown(content), [content]);
  return <>{renderSections(blocks)}</>;
};

export const formatEffectiveDate = (document: LegalDocument): string => {
  const effectiveAt = typeof document.effectiveAt === 'number' && document.effectiveAt < 1_000_000_000_000
    ? document.effectiveAt * 1000
    : document.effectiveAt;

  return new Intl.DateTimeFormat('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Bucharest',
  }).format(new Date(effectiveAt));
};

const LegalDocumentContent: React.FC<LegalDocumentContentProps> = ({ documentType }) => {
  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [noticeError, setNoticeError] = useState(false);
  const noticeId = documentType === 'TERMS' ? new URLSearchParams(window.location.search).get('notice') : null;

  useEffect(() => {
    let active = true;
    setDocument(null);
    setNoticeError(false);
    const documentRequest = noticeId
      ? fetchAnnouncedLegalDocument(noticeId)
      : fetchActiveLegalDocument(documentType);
    documentRequest
      .then(result => {
        if (active) {
          setDocument(result);
        }
      })
      .catch(() => {
        if (active && noticeId) {
          setNoticeError(true);
        }
        // Keep the bundled fallback available if the API is temporarily unavailable.
      });
    return () => {
      active = false;
    };
  }, [documentType, noticeId]);

  if (noticeId && !document) {
    return (
      <p role="status">
        {noticeError
          ? 'Nu am putut încărca versiunea anunțată. Reîncearcă sau scrie-ne la contact@roade.ro.'
          : 'Se încarcă versiunea anunțată...'}
      </p>
    );
  }

  if (!document) {
    const fallback = fallbackDocuments[documentType];
    return fallback ? (
      <>
        <h1 className="legal-title">{fallback.title}</h1>
        <p className="legal-update">Data intrării în vigoare: {fallback.effectiveDate}</p>
        <LegalMarkdownContent content={fallback.content} />
      </>
    ) : null;
  }

  return (
    <>
      <h1 className="legal-title">{document.title}</h1>
      {noticeId && (
        <p className="legal-update">
          Versiunea {document.documentVersion}, comunicată prin email.{' '}
          <a href="/terms">Vezi Termenii în vigoare</a>.
        </p>
      )}
      <p className="legal-update">Data intrării în vigoare: {formatEffectiveDate(document)}</p>
      <LegalMarkdownContent content={document.content} />
    </>
  );
};

export default LegalDocumentContent;
